/*
  card-tilt.js — per-card cursor-driven 3D tilt, holographic shine, and reactive
  shadow for the trading (staff) cards.

  On pointer move it finds the NEAREST .c-card--trading and, if the cursor is
  within a halo around it (HALO px beyond the card edge), tilts that card toward
  the pointer and drives its shine + shadow. The halo means a card starts
  reacting as the cursor approaches, before it's even over it.

  Hit-testing uses cached layout boxes, NOT a per-frame getBoundingClientRect:
  reading the rect every frame would include the live tilt/scale and feed back
  into the next frame (jitter + the active card inflating over its neighbours).
  Instead we measure every card ONCE (getBoundingClientRect + scroll = document
  coords, which are scroll-invariant) while all cards are neutral, and reuse it.
  We only re-measure on resize / load / when the cards are replaced (pagination,
  detected via isConnected), and we neutralise the active card first so its box
  is never captured mid-tilt.

  It writes CSS variables the card's CSS reads:
    --rx / --ry        X/Y rotation (deg) for the perspective tilt
    --holo-pos         horizontal mask position (%) for the holographic band
    --shadow-x / -y    drop-shadow offset (rem) so the shadow shifts with tilt

  Writes are coalesced to one rAF per frame. Touch/pen are ignored (no hover)
  and the whole thing is skipped under prefers-reduced-motion.
*/
(function () {
  'use strict';

  const MAX_TILT = 12; // degrees at the card edge
  const HALO = 70; // px of tracking area beyond each card's edge
  const OVERSHOOT = 0.18; // how far past the edge (0-1) the tilt keeps growing
  const GLINT_MIN_MS = 2600; // shortest gap between idle glints (grid-wide)
  const GLINT_MAX_MS = 5800; // longest gap between idle glints
  const GLINT_DURATION_MS = 1700; // matches the CSS glint animation + margin

  let active = null;
  let pending = null;
  let ticking = false;
  let rects = []; // [{ el, x, y, w, h }] in document coords (scroll-invariant)

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function reset(card) {
    card.style.removeProperty('--rx');
    card.style.removeProperty('--ry');
    card.style.removeProperty('--holo-pos');
    card.style.removeProperty('--shadow-x');
    card.style.removeProperty('--shadow-y');
    card.removeAttribute('data-tilting');
  }

  function measure() {
    // Capture neutral boxes: reset the active card so its rect isn't tilted.
    if (active) {
      reset(active);
      active = null;
    }
    const cards = document.querySelectorAll('.c-card--trading');
    const sx = window.scrollX;
    const sy = window.scrollY;
    rects = [];
    for (let i = 0; i < cards.length; i++) {
      const el = cards[i];
      const r = el.getBoundingClientRect();
      rects.push({ el: el, x: r.left + sx, y: r.top + sy, w: r.width, h: r.height });
    }
  }

  function apply() {
    ticking = false;
    const event = pending;
    if (!event) return;

    if (!rects.length || !rects[0].el.isConnected) measure();
    if (!rects.length) return;

    // Cursor in document coords so it compares against the cached layout boxes.
    const cx = event.clientX + window.scrollX;
    const cy = event.clientY + window.scrollY;

    let nearest = null;
    let nearestDist = Infinity;
    let nx = 0.5;
    let ny = 0.5;

    for (let i = 0; i < rects.length; i++) {
      const r = rects[i];
      const dx = Math.max(r.x - cx, 0, cx - (r.x + r.w));
      const dy = Math.max(r.y - cy, 0, cy - (r.y + r.h));
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = r.el;
        nx = (cx - r.x) / r.w;
        ny = (cy - r.y) / r.h;
      }
    }

    if (active && active !== nearest) {
      reset(active);
      active = null;
    }
    if (!nearest || nearestDist > HALO) {
      if (active) {
        reset(active);
        active = null;
      }
      return;
    }

    active = nearest;
    // Position within the card, allowed to drift slightly past the edges so the
    // tilt keeps building through the halo instead of snapping at the border.
    const px = clamp(nx, -OVERSHOOT, 1 + OVERSHOOT);
    const py = clamp(ny, -OVERSHOOT, 1 + OVERSHOOT);
    const ox = px - 0.5;
    const oy = py - 0.5;

    nearest.style.setProperty('--ry', (ox * 2 * MAX_TILT).toFixed(2) + 'deg');
    nearest.style.setProperty('--rx', (-oy * 2 * MAX_TILT).toFixed(2) + 'deg');
    nearest.style.setProperty('--holo-pos', (clamp(nx, 0, 1) * 100).toFixed(1) + '%');
    // Shadow casts opposite the lean, and deepens as the card tilts.
    nearest.style.setProperty('--shadow-x', (-ox * 1.6).toFixed(2) + 'rem');
    nearest.style.setProperty('--shadow-y', (0.7 - oy * 1.4).toFixed(2) + 'rem');
    nearest.setAttribute('data-tilting', 'true');
  }

  function onMove(event) {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    pending = event;
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(apply);
    }
  }

  function onOut(event) {
    // relatedTarget null means the pointer left the document entirely.
    if (!event.relatedTarget && active) {
      reset(active);
      active = null;
    }
  }

  // Self-heal stale boxes: if the card the pointer just entered has shifted from
  // its cached box (e.g. it lives in a moving carousel marquee), drop the cache
  // so apply() re-measures at the current positions and tilts the right card.
  // Static cards (the grid / list) always match, so their behaviour is unchanged.
  function onOver(event) {
    const target = event.target;
    const card = target && target.closest ? target.closest('.c-card--trading') : null;
    if (!card) return;
    for (let i = 0; i < rects.length; i++) {
      if (rects[i].el !== card) continue;
      const r = card.getBoundingClientRect();
      const dx = Math.abs(r.left + window.scrollX - rects[i].x);
      const dy = Math.abs(r.top + window.scrollY - rects[i].y);
      if (dx > 2 || dy > 2) invalidate();
      return;
    }
  }

  function invalidate() {
    rects = [];
  }

  // Idle glint: at random sparse intervals, pick one idle card and let its CSS
  // glint animation run a single sheen + holo sweep, so the grid catches light
  // on its own. Skips cards being hovered and pauses when the tab is hidden.
  function triggerGlint() {
    const cards = document.querySelectorAll('.c-card--trading');
    if (cards.length && !document.hidden) {
      const card = cards[Math.floor(Math.random() * cards.length)];
      if (
        card.getAttribute('data-tilting') !== 'true' &&
        card.getAttribute('data-glint') !== 'true'
      ) {
        card.setAttribute('data-glint', 'true');
        window.setTimeout(function () {
          card.removeAttribute('data-glint');
        }, GLINT_DURATION_MS);
      }
    }
    scheduleGlint();
  }

  function scheduleGlint() {
    const delay = GLINT_MIN_MS + Math.random() * (GLINT_MAX_MS - GLINT_MIN_MS);
    window.setTimeout(triggerGlint, delay);
  }

  function init() {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    measure();
    document.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerover', onOver, { passive: true });
    document.addEventListener('pointerout', onOut);
    window.addEventListener('resize', invalidate);
    window.addEventListener('load', invalidate);
    scheduleGlint();
  }

  document.addEventListener('DOMContentLoaded', init);
})();
